/**
 * Payment Page
 * Stripe checkout for hotel booking payment
 */

import { t, formatCurrency } from '../i18n';
import { Header, Card, FloatingNav } from '../components';
import { getState, navigateTo, setBooking, setGuestInfo } from '../store';
import { getPaymentConfig, createPaymentIntent, getPaymentMethodDisplay, getPaymentStatus } from '../api';
import type { Stripe, StripeElements } from '@stripe/stripe-js';

let stripeInstance: Stripe | null = null;
let elementsInstance: StripeElements | null = null;
let isInitializing = false;
let currentClientSecret: string | null = null;
/** Guard to prevent double navigation from polling + confirmPayment resolving */
let paymentResolved = false;

const PAYMENT_BOOKING_KEY = 'stripe_return_booking';

/**
 * Render payment page
 */
export function renderPaymentPage(): string {
  const state = getState();
  let { booking } = state;

  // If no booking in state, try to restore from localStorage
  // (handles LIFF webview reload after PromptPay QR opened in external browser)
  if (!booking) {
    const saved = localStorage.getItem(PAYMENT_BOOKING_KEY);
    if (saved) {
      try {
        const savedData = JSON.parse(saved);
        booking = savedData.booking ?? savedData;
      } catch (_) { /* ignore */ }
    }
  }

  if (!booking) {
    // No booking context — return a placeholder div.
    // NEVER call navigateTo() inside a render function — it causes re-render loops.
    // initPaymentPage() will decide: show close-window page or navigate to search.
    return `<div id="payment-placeholder"></div>`;
  }

  const isNonRefundable = booking.isRefundable === false;

  return `
    ${Header(t('paymentTitle'), false)}

    <div style="margin:8px 16px 0; padding:10px 14px; background:#fffbe6; border:1px solid #ffe58f; border-radius:8px; font-size:13px; color:#614700; display:flex; align-items:flex-start; gap:8px;">
      <span style="font-size:16px;">⚠️</span>
      <span>${t('paymentWarningBanner')} — <strong>${t('paymentNoGoBack')}</strong></span>
    </div>
    
    <div class="page-content" id="payment-content">
      ${Card(`
        <h3 class="card-title">${t('paymentTitle')}</h3>
        <div class="summary-section">
          <div class="summary-row">
            <span>${t('confirmationNumber')}</span>
            <span><strong>${booking.confirmationNumber}</strong></span>
          </div>
          <div class="summary-row total-highlight">
            <span>${t('totalAmount')}</span>
            <span class="total-amount">${formatCurrency(booking.totalAmount, booking.currency)}</span>
          </div>
        </div>
      `)}

      <div id="payment-form-container" class="card" style="margin:16px; padding:20px; border-radius:12px; background:#fff; box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <div id="payment-loading" style="text-align:center; padding:30px;">
          <div class="spinner" style="width:32px; height:32px; border:3px solid #e0e0e0; border-top:3px solid #00B900; border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto;"></div>
          <p style="margin-top:12px; color:#666;">${t('loading')}</p>
        </div>

        <div id="stripe-form" style="display:none;">
          <div id="payment-element" style="margin-bottom:20px;"></div>
          
          <div id="payment-message" style="display:none; padding:12px; border-radius:8px; margin-bottom:16px; font-size:14px;"></div>

          <button 
            id="submit-payment" 
            type="button"
            class="btn btn-primary btn-block btn-lg"
            style="margin-bottom:12px;"
          >
            💳 ${t('payNow')}
          </button>
        </div>
      </div>

      ${isNonRefundable
        ? `<div style="margin:0 16px 16px; padding:12px 14px; background:#fee2e2; border:1px solid #fca5a5; border-radius:8px; font-size:13px; color:#991b1b; line-height:1.6;"><strong>${t('nonRefundable')}</strong><br/>${t('nonRefundableWarning')}</div>`
        : `<div style="padding:0 16px;"><button type="button" class="btn btn-secondary btn-block" onclick="window.skipPayment()" style="margin-bottom:16px;">🏨 ${t('skipPayment')}</button></div>`
      }

      <div style="padding:0 16px 20px; text-align:center;">
        <img src="https://stripe.com/img/v3/home/social.png" alt="Powered by Stripe" style="height:24px; opacity:0.5;" onerror="this.style.display='none'">
        <p style="font-size:11px; color:#999; margin-top:4px;">Secure payment powered by Stripe</p>
      </div>
    </div>

    ${FloatingNav()}

    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .total-highlight {
        background: #f0faf0;
        padding: 12px;
        border-radius: 8px;
        margin-top: 8px;
      }
      .total-highlight .total-amount {
        font-size: 20px;
        font-weight: 700;
        color: #00B900;
      }
      #payment-element {
        min-height: 80px;
      }
      .payment-error {
        background: #fff2f0;
        color: #cf1322;
        border: 1px solid #ffa39e;
      }
      .payment-success {
        background: #f6ffed;
        color: #389e0d;
        border: 1px solid #b7eb8f;
      }
      .payment-waiting {
        background: #fffbe6;
        color: #614700;
        border: 1px solid #ffe58f;
      }
    </style>
  `;
}

/**
 * Initialize payment page
 */
export async function initPaymentPage(): Promise<void> {
  // Guard against re-entrant calls triggered by setState re-render
  if (isInitializing) return;
  isInitializing = true;

  // Back button handler — disabled on payment page to prevent confusion
  // (booking is already confirmed; user must pay or skip)
  window.handleBack = () => {
    // Show warning toast instead of navigating
    const existingToast = document.getElementById('payment-back-toast');
    if (existingToast) return;
    const toast = document.createElement('div');
    toast.id = 'payment-back-toast';
    toast.style.cssText = [
      'position:fixed',
      'top:50%',
      'left:50%',
      'transform:translate(-50%,-50%)',
      'background:#614700',
      'color:white',
      'padding:20px 24px',
      'border-radius:12px',
      'font-size:15px',
      'font-weight:600',
      'box-shadow:0 8px 32px rgba(0,0,0,0.35)',
      'z-index:99999',
      'text-align:center',
      'max-width:80%',
    ].join(';');
    toast.innerHTML = `<div style="font-size:28px;margin-bottom:8px;">⚠️</div><div>${t('paymentBackWarning').split('\n').join('<br>')}</div>`;
    document.body.appendChild(toast);
    setTimeout(() => { toast.remove(); }, 2500);
  };

  // Skip payment handler
  window.skipPayment = async () => {
    paymentResolved = true; // stop any running polling
    localStorage.removeItem(PAYMENT_BOOKING_KEY);
    // Mark payment as pay_at_hotel so admin knows
    const state = getState();
    if (state.booking?.confirmationNumber) {
      try {
        const { updatePaymentStatus } = await import('../api');
        await updatePaymentStatus(state.booking.confirmationNumber, 'pay_at_hotel');
      } catch (_) { /* non-critical — proceed regardless */ }
      // Update local booking state so success page Flex Message shows pay-at-hotel
      if (state.booking) {
        setBooking({ ...state.booking, paymentStatus: 'pay_at_hotel' });
      }
    }
    isInitializing = false;
    navigateTo('success');
  };

  // If Stripe Elements already initialized (e.g. after language change re-renders the page),
  // just re-mount the existing elements to the new DOM instead of creating a new PaymentIntent.
  if (currentClientSecret && elementsInstance) {
    const existingPaymentEl = elementsInstance.getElement('payment');
    if (existingPaymentEl) {
      existingPaymentEl.unmount();
      existingPaymentEl.mount('#payment-element');
    }
    const reLdEl = document.getElementById('payment-loading');
    const reFormEl = document.getElementById('stripe-form');
    if (reLdEl) reLdEl.style.display = 'none';
    if (reFormEl) reFormEl.style.display = 'block';
    const reSubmitBtn = document.getElementById('submit-payment');
    if (reSubmitBtn) {
      reSubmitBtn.addEventListener('click', handlePaymentSubmit);
    }
    isInitializing = false;
    return;
  }

  // No booking context — renderPaymentPage returned a placeholder.
  // This happens when Stripe redirects back in a new tab/window (new browsing context)
  // or when navigating directly without a booking.
  const placeholder = document.getElementById('payment-placeholder');
  if (placeholder) {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectStatus = urlParams.get('redirect_status');

    const closePageHtml = (icon: string, title: string, subtitle: string, btnColor: string) => `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;text-align:center;background:#f9f9f9;">
        <div style="font-size:64px;margin-bottom:16px;">${icon}</div>
        <h2 style="color:${btnColor};margin-bottom:8px;font-size:20px;">${title}</h2>
        <p style="color:#666;margin-bottom:24px;font-size:14px;">${subtitle}</p>
        <button onclick="window.close()" style="padding:12px 24px;background:${btnColor};color:white;border:none;border-radius:8px;font-size:16px;cursor:pointer;">ปิดหน้านี้</button>
      </div>
    `;

    if (redirectStatus === 'succeeded') {
      placeholder.outerHTML = closePageHtml('✅', t('paymentSuccessful'), 'กรุณากลับไปยังแอพ LINE ของคุณ', '#00B900');
      setTimeout(() => { window.close(); isInitializing = false; }, 3000);
      return;
    }
    if (redirectStatus === 'failed') {
      placeholder.outerHTML = closePageHtml('❌', 'การชำระเงินไม่สำเร็จ', 'กรุณากลับไปยังแอพ LINE และลองใหม่อีกครั้ง', '#cf1322');
      setTimeout(() => { window.close(); isInitializing = false; }, 3000);
      return;
    }
    // No redirect status and no booking — not a valid payment page entry
    isInitializing = false;
    navigateTo('search');
    return;
  }

  // Handle return from PromptPay — LIFF webview may have reloaded after external
  // browser opened for QR scan. localStorage survives across LIFF browsing contexts.
  const savedStateStr = localStorage.getItem(PAYMENT_BOOKING_KEY);
  if (savedStateStr) {
    // ALWAYS clear localStorage immediately — prevents infinite reload loop
    // regardless of what happens in the async verification below.
    localStorage.removeItem(PAYMENT_BOOKING_KEY);
    
    // Check if we are returning from Red Dot Payment
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('paymentId');
    if (paymentId) {
      try {
        const savedData = JSON.parse(savedStateStr);
        const savedBooking = savedData.booking ?? savedData;
        const savedGuestInfo = savedData.guestInfo ?? null;
        if (savedBooking) setBooking(savedBooking);
        if (savedGuestInfo) setGuestInfo(savedGuestInfo);
        
        if (window.location.search) {
          window.history.replaceState({}, '', window.location.pathname);
        }
        
        const statusResult = await getPaymentStatus(paymentId);
        if (statusResult?.status === 'succeeded' || statusResult?.status === 'paid') {
          showPaymentSuccess(t('paymentSuccessful'));
          const loadingEl = document.getElementById('payment-loading');
          if (loadingEl) loadingEl.style.display = 'none';
          const formEl = document.getElementById('stripe-form');
          if (formEl) formEl.style.display = 'block';
          
          setTimeout(() => {
            const restoredBooking = getState().booking;
            if (restoredBooking) {
              setBooking({ ...restoredBooking, paymentStatus: 'paid' });
            }
            isInitializing = false;
            navigateTo('success');
          }, 1500);
          return;
        } else {
          showPaymentError('การชำระเงินไม่สำเร็จ หรือถูกยกเลิก');
          // Fall through to show the form again
        }
      } catch (e) {
        console.error('[Payment] RDP verify error:', e);
      }
    }
    
    try {
      const savedData = JSON.parse(savedStateStr);
      const savedBooking = savedData.booking ?? savedData;
      const savedGuestInfo = savedData.guestInfo ?? null;
      const clientSecret: string | null = savedData.clientSecret ?? null;

      // Restore booking + guestInfo to state BEFORE any navigation
      if (savedBooking) setBooking(savedBooking);
      if (savedGuestInfo) setGuestInfo(savedGuestInfo);

      // Clear any URL params Stripe may have appended
      if (window.location.search) {
        window.history.replaceState({}, '', window.location.pathname);
      }

      // Only bother verifying if we actually have a clientSecret
      // and we need to detect a 'succeeded' payment that user may have completed.
      if (clientSecret) {
        const config = await getPaymentConfig();
        if (config) {
          const { loadStripe } = await import('@stripe/stripe-js');
          const stripe = await loadStripe(config.publishableKey || '');
          if (stripe) {
            const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
            if (paymentIntent?.status === 'succeeded') {
              showPaymentSuccess(t('paymentSuccessful'));
              const loadingEl = document.getElementById('payment-loading');
              if (loadingEl) loadingEl.style.display = 'none';
              const formEl = document.getElementById('stripe-form');
              if (formEl) formEl.style.display = 'block';
              setTimeout(async () => {
                const restoredBooking = getState().booking;
                if (restoredBooking) {
                  const piId = clientSecret.split('_secret_')[0];
                  const display = await getPaymentMethodDisplay(piId);
                  setBooking({ ...restoredBooking, paymentStatus: 'paid', ...(display ? { paymentMethodDisplay: display } : {}) });
                }
                isInitializing = false;
                navigateTo('success');
              }, 1500);
              return;
            }
            // Any other status (requires_action, processing, canceled, etc.)
            // → fall through to normal init so user gets a fresh form.
          }
        }
      }
      // Fall through: booking+guestInfo are in state, normal init will proceed below.
    } catch (e) {
      console.error('[Payment] Error verifying payment status:', e);
    }
  }

  const state = getState();
  const { booking, guestInfo } = state;

  if (!booking || !guestInfo) {
    isInitializing = false;
    navigateTo('search');
    return;
  }

  try {
    // 1. Get publishable key from backend
    const config = await getPaymentConfig();
    if (!config) {
      showPaymentError('Payment is not available. You can pay at the hotel.');
      return;
    }

    if (config.provider === 'reddot') {
      const loadingEl = document.getElementById('payment-loading');
      const formEl = document.getElementById('stripe-form');
      if (loadingEl) loadingEl.style.display = 'none';
      if (formEl) formEl.style.display = 'block';
      
      const paymentElementDiv = document.getElementById('payment-element');
      if (paymentElementDiv) {
        paymentElementDiv.innerHTML = `<div style="text-align:center; padding: 20px; font-size: 14px; color: #333;">กรุณากดปุ่มชำระเงินด้านล่าง ระบบจะพาท่านไปยังหน้าต่างชำระเงินของ Red Dot Payment</div>`;
      }
      
      const submitBtn = document.getElementById('submit-payment') as HTMLButtonElement;
      if (submitBtn) {
        // Remove old event listeners if any by cloning
        const newSubmitBtn = submitBtn.cloneNode(true) as HTMLButtonElement;
        submitBtn.parentNode?.replaceChild(newSubmitBtn, submitBtn);
        
        newSubmitBtn.addEventListener('click', async () => {
          newSubmitBtn.disabled = true;
          newSubmitBtn.textContent = t('processingPayment');
          hidePaymentMessage();

          const stateBeforeSubmit = getState();
          if (stateBeforeSubmit.booking && stateBeforeSubmit.guestInfo) {
            localStorage.setItem(PAYMENT_BOOKING_KEY, JSON.stringify({
              booking: stateBeforeSubmit.booking,
              guestInfo: stateBeforeSubmit.guestInfo,
            }));
          }

          const amountInSmallestUnit = Math.round(stateBeforeSubmit.booking!.totalAmount * 100);
          const intentResult = await createPaymentIntent({
            confirmationNumber: stateBeforeSubmit.booking!.confirmationNumber,
            amount: amountInSmallestUnit,
            currency: stateBeforeSubmit.booking!.currency || 'thb',
            customerEmail: stateBeforeSubmit.guestInfo!.email,
            customerName: `${stateBeforeSubmit.guestInfo!.firstName} ${stateBeforeSubmit.guestInfo!.lastName}`,
          });

          if (intentResult?.redirectUrl) {
            window.location.href = intentResult.redirectUrl;
          } else {
            localStorage.removeItem(PAYMENT_BOOKING_KEY);
            showPaymentError('Failed to initialize payment.');
            newSubmitBtn.disabled = false;
            newSubmitBtn.textContent = `💳 ${t('payNow')}`;
          }
        });
      }
      isInitializing = false;
      return;
    }

    // 2. Load Stripe.js dynamically
    const { loadStripe } = await import('@stripe/stripe-js');
    stripeInstance = await loadStripe(config.publishableKey || '');

    if (!stripeInstance) {
      showPaymentError('Failed to load payment system.');
      return;
    }

    // 3. Create PaymentIntent
    // Convert amount to smallest currency unit (satang for THB)
    const amountInSmallestUnit = Math.round(booking.totalAmount * 100);

    const intentResult = await createPaymentIntent({
      confirmationNumber: booking.confirmationNumber,
      amount: amountInSmallestUnit,
      currency: booking.currency || 'thb',
      customerEmail: guestInfo.email,
      customerName: `${guestInfo.firstName} ${guestInfo.lastName}`,
    });

    if (!intentResult) {
      showPaymentError('Failed to initialize payment. You can pay at the hotel.');
      return;
    }

    // Store clientSecret for use in handlePaymentSubmit
    currentClientSecret = intentResult.clientSecret;

    // 4. Mount Stripe Elements
    elementsInstance = stripeInstance.elements({
      clientSecret: intentResult.clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#00B900',
          borderRadius: '8px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
      },
    });

    const paymentElement = elementsInstance.create('payment', {
      layout: 'tabs',
      defaultValues: {
        billingDetails: {
          name: `${guestInfo.firstName} ${guestInfo.lastName}`,
          email: guestInfo.email,
        },
      },
    });
    paymentElement.mount('#payment-element');

    // Hide loading, show form
    const loadingEl = document.getElementById('payment-loading');
    const formEl = document.getElementById('stripe-form');
    if (loadingEl) loadingEl.style.display = 'none';
    if (formEl) formEl.style.display = 'block';

    // 5. Handle submit
    const submitBtn = document.getElementById('submit-payment');
    if (submitBtn) {
      submitBtn.addEventListener('click', handlePaymentSubmit);
    }
    // Allow future re-init when language changes re-render the page
    isInitializing = false;
  } catch (error) {
    console.error('[Payment] Init error:', error);
    isInitializing = false;
    showPaymentError('Payment initialization failed. You can pay at the hotel.');
  }
}

/**
 * Handle payment form submission
 */
async function handlePaymentSubmit(): Promise<void> {
  if (!stripeInstance || !elementsInstance) return;
  paymentResolved = false;

  const submitBtn = document.getElementById('submit-payment') as HTMLButtonElement;
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = t('processingPayment');
  }

  hidePaymentMessage();

  // Save state to localStorage before confirmPayment — LIFF may destroy webview
  // when external browser opens for PromptPay QR. localStorage survives this.
  const stateBeforeSubmit = getState();
  if (stateBeforeSubmit.booking && stateBeforeSubmit.guestInfo && currentClientSecret) {
    localStorage.setItem(PAYMENT_BOOKING_KEY, JSON.stringify({
      booking: stateBeforeSubmit.booking,
      guestInfo: stateBeforeSubmit.guestInfo,
      clientSecret: currentClientSecret,
    }));
  }

  // Start background polling BEFORE confirmPayment.
  // For PromptPay, confirmPayment() blocks while QR is displayed and the LIFF
  // webview may be frozen when external browser opens for authorization.
  // Polling runs independently via setTimeout and resumes when webview unfreezes.
  startPaymentPolling();

  const { error } = await stripeInstance.confirmPayment({
    elements: elementsInstance,
    confirmParams: {
      return_url: `${window.location.origin}${window.location.pathname}`,
    },
    redirect: 'if_required',
  });

  // If polling already handled success/failure, skip
  if (paymentResolved) return;

  if (error) {
    if (error.type === 'card_error' || error.type === 'validation_error') {
      paymentResolved = true;
      localStorage.removeItem(PAYMENT_BOOKING_KEY);
      console.error('[Payment] Confirmation error:', error);
      showPaymentError(error.message || t('paymentFailed'));
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = `💳 ${t('payNow')}`;
      }
    }
    return;
  }

  // confirmPayment resolved without error — verify actual PaymentIntent status.
  // For card payments: status is 'succeeded' at this point (safe to proceed).
  // For PromptPay: confirmPayment may resolve without error even when QR is generated
  // but not yet scanned (status = 'requires_action'). Declaring success here would
  // show payment success before any money has moved — polling already handles the
  // real success/failure for these cases, so we just let it continue.
  if (!paymentResolved && currentClientSecret && stripeInstance) {
    try {
      const { paymentIntent: verifiedIntent } = await stripeInstance.retrievePaymentIntent(currentClientSecret);
      if (verifiedIntent?.status === 'succeeded') {
        paymentResolved = true;
        localStorage.removeItem(PAYMENT_BOOKING_KEY);
        showPaymentSuccess(t('paymentSuccessful'));
        setTimeout(async () => {
          const confirmBooking = getState().booking;
          if (confirmBooking) {
            const piId = currentClientSecret?.split('_secret_')[0];
            const display = piId ? await getPaymentMethodDisplay(piId) : null;
            setBooking({ ...confirmBooking, paymentStatus: 'paid', ...(display ? { paymentMethodDisplay: display } : {}) });
          }
          isInitializing = false;
          navigateTo('success');
        }, 1500);
      }
      // else: status is requires_action/processing — polling is still running and
      // will handle the final outcome. Don't navigate to success prematurely.
    } catch (verifyErr) {
      console.error('[Payment] Could not verify payment status after confirmPayment:', verifyErr);
      // Network error during verify — let polling handle it, don't assume success.
    }
  }
}

/**
 * Poll retrievePaymentIntent every 3s until payment is resolved
 */
function startPaymentPolling(): void {
  if (!stripeInstance || !currentClientSecret) return;

  let attempts = 0;
  const MAX_ATTEMPTS = 60; // 60 × 3s = 3 minutes max

  const checkStatus = async () => {
    if (!stripeInstance || !currentClientSecret || paymentResolved) return;

    attempts++;
    if (attempts > MAX_ATTEMPTS) {
      if (paymentResolved) return;
      paymentResolved = true;
      localStorage.removeItem(PAYMENT_BOOKING_KEY);
      showPaymentError('หมดเวลารอการยืนยัน กรุณาตรวจสอบสถานะการชำระเงินอีกครั้ง');
      const submitBtn = document.getElementById('submit-payment') as HTMLButtonElement;
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = `💳 ${t('payNow')}`; }
      return;
    }

    try {
      const { paymentIntent } = await stripeInstance.retrievePaymentIntent(currentClientSecret);
      if (paymentResolved) return;

      if (paymentIntent?.status === 'succeeded') {
        paymentResolved = true;
        localStorage.removeItem(PAYMENT_BOOKING_KEY);
        showPaymentSuccess(t('paymentSuccessful'));
        setTimeout(async () => {
          const pollBooking = getState().booking;
          if (pollBooking) {
            const piId = currentClientSecret?.split('_secret_')[0];
            const display = piId ? await getPaymentMethodDisplay(piId) : null;
            setBooking({ ...pollBooking, paymentStatus: 'paid', ...(display ? { paymentMethodDisplay: display } : {}) });
          }
          isInitializing = false;
          navigateTo('success');
        }, 1500);
        return;
      }

      if (paymentIntent?.status === 'canceled' || paymentIntent?.status === 'requires_payment_method') {
        paymentResolved = true;
        localStorage.removeItem(PAYMENT_BOOKING_KEY);
        showPaymentError(t('paymentFailed'));
        const submitBtn = document.getElementById('submit-payment') as HTMLButtonElement;
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = `💳 ${t('payNow')}`; }
        return;
      }

      // Still processing or requires_action (QR scan pending) — poll again
      setTimeout(checkStatus, 3000);
    } catch (err) {
      console.error('[Payment] Poll error:', err);
      if (!paymentResolved) setTimeout(checkStatus, 3000);
    }
  };

  setTimeout(checkStatus, 3000);
}

/**
 * Show error message in payment form
 */
function showPaymentError(message: string): void {
  const msgEl = document.getElementById('payment-message');
  if (msgEl) {
    msgEl.textContent = message;
    msgEl.className = 'payment-error';
    msgEl.style.display = 'block';
  }

  // Also show the skip button more prominently
  const loadingEl = document.getElementById('payment-loading');
  if (loadingEl) loadingEl.style.display = 'none';
  const formEl = document.getElementById('stripe-form');
  if (formEl) formEl.style.display = 'block';
}

/**
 * Show success message in payment form
 */
function showPaymentSuccess(message: string): void {
  const msgEl = document.getElementById('payment-message');
  if (msgEl) {
    msgEl.textContent = message;
    msgEl.className = 'payment-success';
    msgEl.style.display = 'block';
  }
}


/**
 * Hide payment message
 */
function hidePaymentMessage(): void {
  const msgEl = document.getElementById('payment-message');
  if (msgEl) {
    msgEl.style.display = 'none';
  }
}

declare global {
  interface Window {
    skipPayment: () => void;
  }
}
